import { BookLocation } from '../../../dorian-shared/types/bookLocation/bookLocation';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { MemoryDTO } from '../../../components/pages/Book/MemoryBank/memoryBankTypes';
import { EpisodeTextEntityType } from '../types/episodeTextImportTypes';
import { EpisodeTextImportNode } from './EpisodeTextImportNode';
import { EpisodeTextImportStep } from './EpisodeTextImportStep';

export type ValidateNodeConfig = {
  nodesInImport: EpisodeTextImportNode[];
  nodesInEpisode: Branch[];
  characterNamesInBook: string[];
  bookLocations: BookLocation[];
  memoriesInBook: MemoryDTO[];
  nodeNamesForValidation: string[];
  /** Achievement name (lowercase) → reward ID mapping for PlayerHas validation */
  rewardIds?: Map<string, number>;
};

export class EpisodeTextImport {
  public nodes: EpisodeTextImportNode[];
  private currentNodeIndex = -1;
  constructor() {
    this.nodes = [];
  }

  public addNode(node: EpisodeTextImportNode) {
    this.nodes.push(node);
    this.currentNodeIndex = this.nodes.length - 1;
  }

  public addStep(step: EpisodeTextImportStep) {
    this.nodes[this.currentNodeIndex]?.addStep(step);
  }

  public addConditionToLastConditionListCheck(condition: EpisodeTextImportStep) {
    const currentNode = this.nodes[this.currentNodeIndex];
    if (currentNode) {
      const lastStep = currentNode.lastStep;
      if (lastStep && lastStep.type === EpisodeTextEntityType.CheckConditionList) {
        lastStep.conditionListItems.push(condition);
      }
    }
  }

  /**
   * Validates all nodes/steps and returns flat table rows.
   * Side effect: resets `this.errors = []` on all nodes/steps via `validate()`.
   * Always read errors from the returned flat rows, never from `node.errors` or `step.errors` directly after calling this method.
   * @param skipNodeConflictCheck - When true, skips checking if import node names
   *   conflict with existing episode node names. Used in selective-edit mode where
   *   the import represents the episode's own nodes (not new imports).
   */
  public validateAndCreateTable(
    nodesInEpisode: Branch[],
    characterNamesInBook: string[],
    bookLocations: BookLocation[],
    memoriesInBook: MemoryDTO[],
    skipNodeConflictCheck?: boolean,
    rewardIds?: Map<string, number>,
  ) {
    const newTableData = [];
    const config = this.buildValidateNodeConfig(
      nodesInEpisode, characterNamesInBook, bookLocations, memoriesInBook, skipNodeConflictCheck, rewardIds,
    );

    for (let nodeIndex = 0; nodeIndex < this.nodes.length; nodeIndex++) {
      newTableData.push(...this.validateAndCreateTableForNode(this.nodes[nodeIndex], config));
    }

    // ID integrity validation — only in Selective Edit mode (skip + at least one real ID)
    if (skipNodeConflictCheck && this.nodes.some((n) => n.realBranchId != null)) {
      this.validateIdIntegrity(newTableData);
    }

    return newTableData;
  }

  /**
   * Builds table rows without running any validation — errors are empty arrays.
   * Used for instant render on modal open; validation runs per-node on edit
   * or full via the Validate button.
   */
  public createTableWithoutValidation() {
    const newTableData = [];

    for (const node of this.nodes) {
      newTableData.push({
        id: node.id,
        nodeOrSpeaker: node.title,
        text: node.text,
        description: node.description,
        type: EpisodeTextEntityType.Node,
        errors: [] as string[],
        commands: node.commands,
      });

      for (const step of node.steps) {
        const isLastStep = node.lastStep?.id === step.id;
        newTableData.push({
          id: step.id,
          nodeOrSpeaker: step.speakerName,
          text: step.speakerText,
          description: step.description,
          type: step.type,
          isLastStep,
          errors: [] as string[],
          commands: step.commands,
        });

        if (step.type === EpisodeTextEntityType.CheckConditionList && step.conditionListItems.length > 0) {
          for (const condition of step.conditionListItems) {
            newTableData.push({
              id: condition.id,
              nodeOrSpeaker: condition.speakerName,
              text: condition.speakerText,
              description: condition.description,
              type: condition.type,
              isLastStep: false,
              errors: [] as string[],
              commands: condition.commands,
            });
          }
        }
      }
    }

    return newTableData;
  }

  /**
   * Validates that all real IDs are present and unique across the import.
   * Pushes errors into the flat row data for UI display.
   * Always pass the full flat rows from all nodes to get complete duplicate detection.
   */
  public validateIdIntegrity(tableData: Array<{ id: string; type?: EpisodeTextEntityType; errors: string[] }>) {
    const rowById = new Map(tableData.map((r) => [r.id, r]));
    const seenBranchIds = new Set<number>();
    const seenStepIds = new Set<number>();
    const seenAnswerIds = new Set<number>();

    for (const node of this.nodes) {
      const nodeRow = rowById.get(node.id);
      if (node.realBranchId == null) {
        if (nodeRow) nodeRow.errors.push('Missing real branch ID — node cannot be saved');
      } else {
        if (seenBranchIds.has(node.realBranchId)) {
          if (nodeRow) nodeRow.errors.push(`Duplicate branch ID: ${node.realBranchId}`);
        }
        seenBranchIds.add(node.realBranchId);
      }

      for (const step of node.steps) {
        if (step.type === EpisodeTextEntityType.GotoFromNode || step.type === EpisodeTextEntityType.CheckCondition) {
          continue;
        }

        const stepRow = rowById.get(step.id);
        if (step.type === EpisodeTextEntityType.Answer) {
          if (step.realAnswerId == null) {
            if (stepRow) stepRow.errors.push('Missing real answer ID — answer cannot be saved');
          } else {
            if (seenAnswerIds.has(step.realAnswerId)) {
              if (stepRow) stepRow.errors.push(`Duplicate answer ID: ${step.realAnswerId}`);
            }
            seenAnswerIds.add(step.realAnswerId);
          }
        } else {
          if (step.realStepId == null) {
            if (stepRow) stepRow.errors.push('Missing real step ID — step cannot be saved');
          } else {
            if (seenStepIds.has(step.realStepId)) {
              if (stepRow) stepRow.errors.push(`Duplicate step ID: ${step.realStepId}`);
            }
            seenStepIds.add(step.realStepId);
          }
        }
      }
    }
  }

  /**
   * Validates a single node and its steps, returning flat table rows.
   * Extracted from `validateAndCreateTable()` for per-node reparse support.
   */
  public validateAndCreateTableForNode(
    node: EpisodeTextImportNode,
    config: ValidateNodeConfig,
  ) {
    const nodeErrors = node.validate({
      nodesInImport: config.nodesInImport,
      nodeNamesInEpisode: config.nodeNamesForValidation,
      bookLocations: config.bookLocations,
    });

    const nodeData = {
      id: node.id,
      nodeOrSpeaker: node.title,
      text: node.text,
      description: node.description,
      type: EpisodeTextEntityType.Node,
      errors: nodeErrors,
      commands: node.commands,
    };

    const stepsData = node.steps.flatMap((step) => {
      const isLastStep = node.lastStep?.id === step.id;
      const stepErrors = step.validate({
        nodesInImport: config.nodesInImport,
        nodesInEpisode: config.nodesInEpisode,
        characterNamesInBook: config.characterNamesInBook,
        memoriesInBook: config.memoriesInBook,
        bookLocations: config.bookLocations,
        rewardIds: config.rewardIds,
      });

      const mainStepData = {
        id: step.id,
        nodeOrSpeaker: step.speakerName,
        text: step.speakerText,
        description: step.description,
        type: step.type,
        isLastStep,
        errors: stepErrors,
        warning: step.backgroundWarning,
        commands: step.commands,
      };

      // If this is a CheckConditionList step, include its condition items in the table.
      // Condition errors are already populated by _validateCheckCommand of the parent step —
      // do NOT call condition.validate() here as it resets errors (this.errors = []),
      // wiping out the parent's validation (operator checks, goto checks, duplicates, etc.).
      if (step.type === EpisodeTextEntityType.CheckConditionList && step.conditionListItems.length > 0) {
        const conditionStepsData = step.conditionListItems.map((condition) => ({
          id: condition.id,
          nodeOrSpeaker: condition.speakerName,
          text: condition.speakerText,
          description: condition.description,
          type: condition.type,
          isLastStep: false,
          errors: condition.errors,
          commands: condition.commands,
        }));

        return [mainStepData, ...conditionStepsData];
      }

      return [mainStepData];
    });

    return [nodeData, ...stepsData];
  }

  /**
   * Builds a ValidateNodeConfig for use with `validateAndCreateTableForNode`.
   * Encapsulates the name-filtering logic to avoid duplication.
   */
  public buildValidateNodeConfig(
    nodesInEpisode: Branch[],
    characterNamesInBook: string[],
    bookLocations: BookLocation[],
    memoriesInBook: MemoryDTO[],
    skipNodeConflictCheck?: boolean,
    rewardIds?: Map<string, number>,
  ): ValidateNodeConfig {
    const episodeNodeNames = nodesInEpisode.map((el) => el.title);
    let nodeNamesForValidation: string[];
    if (skipNodeConflictCheck) {
      const importTitlesLower = new Set(this.nodes.map((n) => n.title.toLowerCase().trim()));
      nodeNamesForValidation = episodeNodeNames.filter(
        (title) => !importTitlesLower.has(title.toLowerCase().trim()),
      );
    } else {
      nodeNamesForValidation = episodeNodeNames;
    }
    return {
      nodesInImport: this.nodes,
      nodesInEpisode,
      characterNamesInBook,
      bookLocations,
      memoriesInBook,
      nodeNamesForValidation,
      rewardIds,
    };
  }

  public moveEndingStepsToLastNode() {
    const endingSteps = this.nodes.filter((node) => node.steps.some((step) => step.type === EpisodeTextEntityType.Ending));
    if (endingSteps.length > 0) {
      const nodesWithoutEndingType = this.nodes.filter((node) => !node.steps.some((step) => step.type === EpisodeTextEntityType.Ending));
      this.nodes = [...nodesWithoutEndingType, ...endingSteps];
    }
  }

  /**
   * Replaces unstable shortUUID keys on GotoFromNode and CheckCondition steps
   * with deterministic keys derived from their parent's realId and position.
   *
   * GotoFromNode: `goto:${node.realBranchId}` — unique per node (one gotoBranchId per branch).
   * CheckCondition: `cond:${checkListStep.realStepId}:${index}` — positionally stable within
   *   a CheckConditionList (user can edit values but cannot add/remove switch cases via field edit).
   *
   * Must be called after `moveEndingStepsToLastNode()` so node order is final.
   * Stable keys fix dirty-tracking and revertNode for these structural (non-step) rows.
   */
  public assignDeterministicIds(): void {
    for (const node of this.nodes) {
      for (const step of node.steps) {
        if (step.type === EpisodeTextEntityType.GotoFromNode) {
          step.id = node.realBranchId != null ? `goto:${node.realBranchId}` : step.id;
        }
        if (step.type === EpisodeTextEntityType.CheckConditionList) {
          step.conditionListItems.forEach((cond, idx) => {
            cond.id = step.realStepId != null ? `cond:${step.realStepId}:${idx}` : cond.id;
          });
        }
      }
    }
  }
}
